import * as React from 'react';
import axios from 'axios';
import { CMJN, Color, GalleryFile } from './iMonCompte';
import { renderFilArianne, renderTitleH1 } from './RenderCommon';
import { getUserId, sessionExpire } from './MonCompteAction';
import { formatDate, formatNumCommande } from './OrderAction';
import {getHostApi, getRequestConfig} from '../../utils';

interface RenderGalleryState {
  files?: GalleryFile[];
  selectedFile?: GalleryFile | null;
}

interface RenderGalleryProps {
  surfaceId?: string;
  emplacementId?: string;
  page?: string;
  selectedFile?: GalleryFile;
  orderId: string;
  color?: Color;
  marquage: string;

  onSelectedFilesUpdated?(selectedFile: GalleryFile): void;
}

export default class RenderGallery extends React.Component<RenderGalleryProps, RenderGalleryState> {
  constructor(props: RenderGalleryProps) {
    super(props);
    this.state = {
      selectedFile: null,
    };
  }

  componentDidMount() {
    if (this.props.surfaceId) {
      this._getFilesBySurfaceId();
    } else {
      this._getFiles();
    }
    if (this.props.selectedFile) { this.setState({ selectedFile: this.props.selectedFile }); }
  }

  /**
   * Fonction retournant les tous les fichiers uploadés par un client
   *
   * @private
   */
  _getFiles() {
    axios.get(`${getHostApi()}/customers/${getUserId()}/galleries/`,
      getRequestConfig()).then((response) => {
      if (response.data.files) {
        this.setState({ files: response.data.files });
      }
    }).catch((error) => {
      if (error.response.status === 401) { sessionExpire(); }
    });
  }

  /**
   * Fonction retournant les fichiers déjà uploadés par un client et réutilisables pour la nouvelle surface commandée
   *
   * @private
   */
  _getFilesBySurfaceId() {
    axios.post(`${getHostApi()}/customers/${getUserId()}/orders/${this.props.orderId}/images/surfaces/${this.props.surfaceId}`, {
        cmjn: this.props.color?.cmjn, code1: this.props.color?.code1,
        marquage: this.props.marquage
      }, getRequestConfig()).then((response) => {
        if (response.data.files) { this.setState({ files: response.data.files }); }
      }).catch((error) => {
        if (error.response.status === 401) { sessionExpire(); }
      });
  }

  /**
   * Fonction permettant de convertir une couleur CMJN en RVB
   *
   * @param {any[]} cmjn
   * @returns {string}
   * @private
   */
  static _convertCMJNtoRVB(cmjn: CMJN) {
    const c = cmjn.cyan / 100;
    const m = cmjn.magenta / 100;
    const j = cmjn.jaune / 100;
    const n = cmjn.noir / 100;

    /**
     * Pour les tests changer les valeurs et vérifier le rgb obtenu sur un convertisseur en ligne
     */
    /* const c = 75 / 100;
     const m = 45 / 100;
     const j = 10 / 100;
     const n = 20 / 100; */

    const r = Math.round((1 - Math.min(1, c * (1 - n) + n)) * 255);
    const v = Math.round((1 - Math.min(1, m * (1 - n) + n)) * 255);
    const b = Math.round((1 - Math.min(1, j * (1 - n) + n)) * 255);

    return 'rgb(' + String(r + ', ' + v + ', ' + b) + ')';
  }

  /**
   * Fonction permettant de modifier le style de l'image sélectionnée dans la modal depuis l'envoi de fichiers
   *
   * @param {string} path
   * @private
   */
  _changeStyle = (path: string) => {
    const div: any = document.getElementById(path);
    const divs: any = document.getElementsByClassName('imgGallery');

    for (let i = 0 ; i < divs.length ; i++) {
      const div = divs[i];
      div.children[0].style.border = '1px solid #dee2e6';
      div.children[0].style.boxShadow = 'none';
      div.children[1].style.border = '1px solid #dee2e6';
      div.children[1].style.boxShadow = 'none';
    }

    div.children[0].style.border = '3px solid black';
    div.children[0].style.boxShadow = '0 0px 15px 2px grey';
    div.children[1].style.border = '3px solid black';
    div.children[1].style.boxShadow = '0 0px 15px 2px grey';
  }

  _changePositionBtnModale = () => {
    const blocBtnModale = document.getElementById('btnValidationGallery');
    blocBtnModale?.classList.add('btnValidationGalleryAbsolute');
  }

  _selectFile = (event: React.MouseEvent<HTMLDivElement>, image: GalleryFile) => {
    if (this.props.page === 'modal') {
      this.setState({
        // @ts-ignore
        selectedFile: {
          ...this.state.selectedFile,
          DateDeCommande: image.DateDeCommande,
          cmjn: image.cmjn,
          creation_id: image.creation_id,
          dimensions: image.dimensions,
          fileNameOrigin: image.fileNameOrigin,
          fromOrderId: image.fromOrderId,
          name: image.name,
          numCommande: image.numCommande,
          path: image.path,
          poids: image.poids,
          surface_id: image.surface_id,
          zonesMarquage: image.zonesMarquage,
        },
      }, () => {
        this._changeStyle(image.name);
        this._changePositionBtnModale();
        this._sendSelectedFiles();
      });
    }
  }

  /**
   * Fonction permettant de transmettre le visuel sélectionné au composant parent (ici RenderFileLine.tsx)
   *
   * @private
   */
  _sendSelectedFiles = () => {
    if (this.props.onSelectedFilesUpdated) { this.props.onSelectedFilesUpdated(this.state.selectedFile as GalleryFile); }
  }

  render() {
    const defineFontSize = () => {
      let style = {};

      if (this.props.page === 'modal') { style = { fontSize: '11px' }; }

      return style;
    };

    /**
     * Retourne la miniature de l'image
     *
     * @param {Image} image
     * @param {string} couleur
     * @returns {any}
     */
    const renderImage = (image: GalleryFile) => {
      const newCouleur = '#' + this.props.color?.code1;

      return (
        <div className="d-flex align-items-center border-bottom-0"
             style={{ minHeight: '187px', background: newCouleur, border: '1px solid #dee2e6' }}>
          <img src={getHostApi() + '/customers/' + getUserId() + '/orders/' + this.props.orderId +
          '/image-files3?numCommande=' + image.numCommande + '&id=' + image.creation_id + '&num=' + image.surface_id +
          '&type=miniatureTiff&idCaddies=' + image.idCaddies + '&xsrf=' + localStorage.getItem('xsrfToken')} width={image.dimensions.width}
               style={{ maxWidth: '111px' }} className="mx-auto p-1" />
        </div>
      );
    };

    /**
     * Retourne le nom du fichier tiff à réutiliser
     *
     * @param {Image} image
     * @returns {any}
     */
    const renderTiff = (image: GalleryFile) => {
      if (image.fileNameOrigin && (image.fileNameOrigin.indexOf("tiff") === -1 ||
        JSON.parse(sessionStorage.getItem('shopInfos') as string).env === 'development')) {
        return (
          <p className="m-0 p-1" style={defineFontSize()}>{image.fileNameOrigin} </p>
        );
      }
    };

    /**
     * Retourne la date et le numéro d'une commande
     *
     * @returns {any}
     * @param image
     */
    const renderOrderDateAndNum = (image: GalleryFile) => {
      return (
        <p className="small m-0 p-1" style={defineFontSize()}>
          Utilisé dans la commande <span className="bold">{formatNumCommande(image.numCommande)}</span> du
          &nbsp;{formatDate(image.DateDeCommande.split(' ')[0])}
        </p>
      );
    };

    /**
     * Retourne la couleur du produit précèdemment commandé pour un visuel
     *
     * @param {Image} image
     * @returns {any}
     */
    const renderPreviousColor = (image: GalleryFile) => {
      const rvb = RenderGallery._convertCMJNtoRVB(image.cmjn);
      const style = { height: '20px', background: rvb };
      const styleOverflow: any = { overflow: 'auto' };

      return (
        <div className="d-flex row m-0 align-items-center" style={defineFontSize()}>
          {JSON.parse(sessionStorage.getItem('shopInfos') as string).env === 'development' &&
          <div style={styleOverflow}  className="p-1">
          <div style={style} className="col-md-1 p-0"></div>
          <div className="small pl-1 pr-0 col-md-11">: précèdemment commandé</div>
            <div>{image.luminance?.difference}</div>
            <div>{image.luminance?.differenceCode1}</div>
            <div> CMJN {image.cmjn.cyan}-{image.cmjn.magenta}-{image.cmjn.jaune}-{image.cmjn.noir}</div>
            <div> Signature {image.signature}</div>
          </div>
          }
        </div>
      );
    };

    /**
     * Rendu de la liste des surfaces en spécifiant si le marquage est possible en fonction du visuel choisi
     *
     * @param {any[]} zonesMarquage
     * @returns {any}
     */
    /* const renderEmplacementsPossibles = (zonesMarquage: any[]) => {
      let result = [];

      for (const zone of zonesMarquage) {
        if (zone[1] === true) {
          result.push(
            <span className="mr1 small">
              <Icons icon="done" size={10} color="#73ad22" />&nbsp;{zone[0]}
            </span>,
          );
        } else {
          result.push(
            <span className="mr1 small">
              <Icons icon="block" size={10} color="tomato" />&nbsp;{zone[0]}
            </span>,
          );
        }
      }

      if (this.props.page !== 'modal') {
        return (<div className="d-flex flex-row row w100 ma0 pa05">{result}</div>);
      }
    }; */

   /* const renderFileSize = (image: GalleryFile) => {
      return (
        <div className="d-flex row m-0 p-1 align-items-center" style={defineFontSize()}>
          <div>Poids : {(Number(image.poids) / 100000).toFixed(2)} Mo</div>
        </div>
      );
    };*/

    /**
     * Rendu des informations d'une image
     * @param image
     */
    const renderInfosImage = (image: GalleryFile) => {
      return (
        <div className="border-top-0" style={{ background: '#f2f2f2', border: '1px solid #dee2e6' }}>
          {renderTiff(image)}
          {renderOrderDateAndNum(image)}
          {/* renderFileSize(image) */}
          {renderPreviousColor(image)}
          { /* renderEmplacementsPossibles(image.zonesMarquage) */}
        </div>
      );
    };

    const renderContainerImage = (image: GalleryFile) => {
      return (
        <div className={`col-lg-2 col-md-3 col-6 p-2 imgGallery ${!image.luminance?.isOk ? 'disabled' : ''}`}
             title={
               !image.luminance?.isOk ? 'Ce visuel n\'est pas disponible pour la couleur sélectionnée.' : ''
             }
             id={image.name} key={image.name}
             onClick={event => image.luminance?.isOk ? this._selectFile(event, image) : ''}>
          {renderImage(image)}
          {renderInfosImage(image)}
        </div>
      );
    };

    const renderLoader = () => {
      return (
        <div key={-1} className="text-center bold font16 p-1 w90 mx-auto">
          <div className="m-0 mt7 mt-5 border border-dark p-2">
            <div className="spinner mr-2">
              <div className="bounce1"></div>
              <div className="bounce2"></div>
              <div className="bounce3"></div>
            </div>
            <div className="bold">Chargement</div>
          </div>
        </div>
      );
    };

    const renderImages = () => {
      const tabImages = [];

      if (this.state.files) {
        for (const image of this.state.files) {
          tabImages.push(renderContainerImage(image));
        }
      } else {
        tabImages.push(renderLoader());
      }

      if (tabImages.length === 0) {
        tabImages.push(
          <div key={-2} className="text-center bold font16 p-1 w90 mx-auto">
            <p className="m-0 py-4 border border-black p-1">
              Aucun fichier n'est disponible pour cette surface de marquage
            </p>
          </div>,
        );
      }

      return (
        <div className="d-flex row align-items-center m-0">
          {tabImages}
        </div>
      );
    };

    /**
     * Rendu de la Galerie d'images
     */
    const renderGallery = () => {
      return (
        <div>
          {renderImages()}
        </div>
      );
    };

    /**
     * Rendu du titre et du fil d'arianne
     */
    const renderTitleAndArianne = () => {
      if (this.props.page != 'modal') {
        return (
          <div>
            {renderTitleH1('collections', 'Ma galerie')}
            {renderFilArianne('', 'Ma galerie')}
          </div>
        );
      }
    };

    return (
      <div>
        {renderTitleAndArianne()}
        {renderGallery()}
      </div>
    );
  }
}
